import dayjs, { Dayjs } from "dayjs";
import { LoginUserItem, SelectedEventsProps } from "@/types";
import { useEffect, useState, Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import cn from "classnames";
import { useModalAction } from "@/contexts/modal/Context.tsx";
import routes from "@/config/routes.ts";
import { useUserSetChangeEventsMutation } from "@/data/user.ts";
import { SpinnerLoader } from "@/components/ui/Loader.tsx";
import IntercomHelpIcon from "@/components/icons/IntercomHelpIcon.tsx";
import CheckIcon from "@/components/icons/agenda/CheckIcon.tsx";
import { t } from "i18next";
import EventCheckIcon from "@/components/icons/EventCheckIcon";

interface ChangeEventDropdownProps {
  isLoadingEvents: boolean;
  currentDateEvent: Dayjs;
  dates: SelectedEventsProps[];
  user: LoginUserItem;
  eventId: number;
  eventCount: number;
  className?: string;
  isMobile?: boolean;
  isDropDown?: boolean;
  setIsDropDown?: Dispatch<SetStateAction<boolean>>;
}

const ChangeEventDropdown = ({
  isLoadingEvents,
  currentDateEvent,
  dates,
  eventId,
  eventCount = -3,
  user,
  isMobile = false,
  className,
  isDropDown,
  setIsDropDown,
}: ChangeEventDropdownProps) => {
  const [selectedValue, setSelectedValue] = useState<
    SelectedEventsProps | undefined
  >();
  const [confirmSelection, setConfirmSelection] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [showPpup, setShowPopup] = useState<boolean>(false);
  const [showCheck, setShowCheck] = useState<boolean>(false);

  const { closeModal } = useModalAction();

  const {
    mutate: setChangeEvent,
    isLoading,
    isSuccess,
    data,
  } = useUserSetChangeEventsMutation();
  const navigate = useNavigate();

  const handleValue = (value: SelectedEventsProps) => {
    setSelectedValue(value);
    // setOpen(false);
  };

  const handleSubmit = () => {
    if (!selectedValue) return;

    const params = {
      newEvent: selectedValue.eventId,
      userToken: user.token,
      event: eventId,
    };
    setChangeEvent(params);
  };

  useEffect(() => {
    if (isSuccess && data) {
      if (!isMobile) {
        closeModal();
      } else {
        navigate(routes.agenda);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isMobile, isSuccess]);

  useEffect(() => {
    // Sync `isDropDown` state with `open`
    if (setIsDropDown) {
      setIsDropDown(open);
    }
  }, [open, setIsDropDown]);

  const nowFormatted = dayjs().format("YYYY-MM-DD HH:mm:ss");
  const eventTimeFormatted = currentDateEvent.format("YYYY-MM-DD HH:mm:ss");

  const now = dayjs(nowFormatted);
  const eventTime = dayjs(eventTimeFormatted);
  const minutesDifference = eventTime.diff(now, "minute");

  const isAtLeastOneHourBefore = minutesDifference >= 60;

  if (!isAtLeastOneHourBefore) {
    return null;
  }

  const id = open ? "change-event-dropdown" : undefined;
  return (
    <div className="relative">
      <button
        onClick={() => {
          setOpen((prev) => !prev);
        }}
        disabled={
          confirmSelection || dayjs().add(1, "hours").isAfter(currentDateEvent)
        }
        className={cn(
          "w-full relative flex flex-row items-center justify-between px-3 h-[40px]",
          className,
          "rounded-lg",
          "border-[1px]",
          dayjs().add(1, "hours").isAfter(currentDateEvent)
            ? "bg-light-250"
            : "bg-white",
          selectedValue ? "border-[1px]" : "border",
          selectedValue
            ? confirmSelection
              ? "border-brand-green"
              : "border-brand-deep"
            : "border-edu-active",
          "relative",
          isDropDown ? "hidden" : "",
        )}
      >
        <p className={"text-left"} aria-describedby={id}>
          <span
            className={cn("font-[Inter] font-semibold text-primary", {
              "text-base": !selectedValue,
              "text-[14px]": selectedValue,
            })}
          >
            {selectedValue
              ? t("agenda.change_event")
              : t("agenda.change_event")}
          </span>
        </p>
        {confirmSelection ? (
          <CheckIcon className={"w-6 h-auto"} />
        ) : (
          <button
            disabled={!selectedValue || isLoading}
            className={
              "font-semibold flex-shrink-0 rounded-lg px-2 py-1 bg-brand-deep text-white text-[10px]"
            }
          >
            {eventCount > 1
              ? t("agenda.count_events", {
                  count: eventCount,
                })
              : t("agenda.count_event", {
                  count: eventCount,
                })}
          </button>
        )}
      </button>
      {open && (
        <div
          id="animatedDiv"
          className={cn(
            "absolute top-0 bg-white my-3 x-0 pt-2 pb-0 border-[1px] rounded w-full border-brand transition-all duration-300",
            { "max-h-0 opacity-0": !open, "max-h-96 opacity-100": open },
          )}
        >
          <div className={"flex flex-col text-14px h-full gap-2 rounded-md"}>
            {isLoadingEvents ? (
              <div className="mx-auto">
                <SpinnerLoader />
              </div>
            ) : eventCount > 0 ? (
              dates && dates.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5 px-2 overflow-y-auto max-h-[100px] min-h-[100px]">
                    {dates.map((r, ix) => (
                      <button
                        onClick={() => handleValue(r)}
                        key={r.eventId + ix}
                        className={`font-semibold  text-center px-1   rounded-[5px] text-[10px] pointer-events-auto py-1.5 h-[28px] ${
                          selectedValue?.eventId === r.eventId
                            ? "bg-[#8065C9] text-white"
                            : "bg-[#E6E6E6]"
                        }`}
                      >
                        {dayjs(r.date).format("ddd D MMM, HH:mm")}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <p className={"px-3 text-brand-secondary text-14px"}>
                  {t("agenda.no_events")}
                </p>
              )
            ) : (
              <p className={"px-3 text-brand-secondary text-14px"}>
                {t("agenda.cant_change_events")}
              </p>
            )}
            <div
              className={
                "w-full flex flex-row gap-2 items-center justify-between text-brand font-semibold px-2 py-1  max-h-[6vh] min-h-[6vh] "
              }
            >
              <div className="flex gap-2">
                <IntercomHelpIcon className={"w-6 h-auto"} />
                <p className={"text-[14px] font-normal text-primary"}>
                  {t("agenda.need_help")}
                </p>
              </div>
              <div className={`flex gap-3 ${showPpup ? "hidden" : ""}`}>
                <button
                  onClick={() => {
                    setOpen((prev) => !prev);
                    setSelectedValue(undefined);
                  }}
                  className="bg-[#E6E6E6] text-black font-bold text-sm rounded-[5px] px-4 py-1"
                >
                  {t("btns.back")}
                </button>
                <button
                  onClick={() => {
                    setShowPopup(true);
                  }}
                  disabled={!selectedValue}
                  className={`bg-[#74DFAC] ${
                    selectedValue
                      ? "bg-opacity-100"
                      : "bg-opacity-40 cursor-not-allowed text-gray-500"
                  } text-black font-bold text-sm rounded-[5px] px-2 py-1`}
                >
                  {t("btns.confirm")}
                </button>
              </div>
              {showPpup && (
                <div className="flex items-center gap-3">
                  <span className="text-[12px] font-medium text-[#2D224C]">
                    Are you sure?
                  </span>
                  <button
                    onClick={() => {
                      setShowPopup(false);
                    }}
                    className="px-4 py-1 rounded-[5px] text-[12px] font-semibold bg-[#FE9595] text-[#2D224C] transition-colors "
                  >
                    {t("btns.no")}
                  </button>
                  <button
                    onClick={() => {
                      setConfirmSelection(true);
                      handleSubmit();
                      setShowPopup(false);
                      setShowCheck(true);
                    }}
                    className="px-3 py-1 rounded-[5px] text-[12px] font-semibold bg-[#74DFAC] text-[#2D224C]  transition-colors "
                  >
                    {t("btns.yes")}
                  </button>
                </div>
              )}
            </div>
            {showCheck && (
              <div className="absolute inset-0 flex items-center justify-center bg-opacity-100 bg-white z-50 flex-col gap-6">
                {" "}
                <EventCheckIcon />
                <h1 className="text-[16px] font-semibold">
                  {t("btns.event_changed")}
                </h1>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
export default ChangeEventDropdown;
